/**
 * TEC navbar redesign Phase 4 item 1: the single source of truth for every row-one/row-two
 * control's class strings, shared by the hydrated header (NavigationNavbar's live-index branch
 * and its nav-live-index/* components) and the prerendered static twin (ResourceNavbar.tsx +
 * the vanilla-JS island it injects, web-static/pages/landing/landing-user-menu-script.ts).
 *
 * Before this file, the two renderers kept independent copies of the same class strings and
 * drifted (see the Phase 4 spec's side-by-side DOM diff). Every export here is a plain string
 * (or a small pure function returning one) with no React/hooks dependency, so it is safe to
 * import from both a React component and a plain TS string-template script.
 *
 * `data-jc-nav-part="<name>"` is the drift-guard hook: both renderers stamp the same part name
 * on the equivalent element, and components/__tests__/primary-nav-uniformity.test.tsx renders
 * both to static markup and asserts the `class` attribute is byte-identical for every part, in
 * all three locales. Changing a class string here (or in only one renderer) must fail that test
 * unless both sides are updated together.
 */

export const NAV_PART = {
  logoHorizontal: 'logo-horizontal',
  logoCompact: 'logo-compact',
  mobileSubtitle: 'mobile-subtitle',
  searchWrapper: 'search-wrapper',
  searchInput: 'search-input',
  locationSegment: 'location-segment',
  searchFiltersChip: 'search-filters-chip',
  searchGoButton: 'search-go-button',
  langTrack: 'lang-track',
  langCircle: 'lang-circle',
  accountPill: 'account-pill',
  accountAvatar: 'account-avatar',
  menuButton: 'menu-button',
  stripFigure: 'strip-figure',
  stripFigureValue: 'strip-figure-value',
  stripWord: 'strip-word',
  stripDivider: 'strip-divider',
  stripStamp: 'strip-stamp',
  // TEC navbar redesign item 2: the mobile bottom bar's own parts. Kept distinct from
  // searchWrapper/searchInput above (row one's search bar) rather than reused: the two bars use
  // different sizeVariants (compact44 vs compact40), so sharing a part name would make the
  // drift-guard's "every instance of a part shares one class string" rule fail for a legitimate
  // reason (different heights), not a real drift.
  mobileBottomBar: 'mobile-bottom-bar',
  mobileBottomBarFilters: 'mobile-bottom-bar-filters',
  mobileBottomBarMenu: 'mobile-bottom-bar-menu',
} as const;

// ── Logo (matches components/navigation/Logo.tsx variant="horizontal"/"compact" and
// NavigationNavbar's own inline mobile block) ──
export const LOGO_HORIZONTAL_WRAP =
  'hidden xl:inline-flex xl:items-center gap-2 cursor-pointer h-10 w-auto rounded-xl transition-colors hover:bg-jc-elevated';
export const LOGO_HORIZONTAL_IMG_PX = 40;
export const LOGO_HORIZONTAL_TEXT_CLASS = 'text-xl';

// Below xl: the logo spans both lines and the wordmark and live line stack beside it (a grid, so the
// logo lines up with the whole block instead of only the wordmark row).
export const LOGO_COMPACT_WRAP =
  'xl:hidden grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-0.5 min-w-0 cursor-pointer';
export const LOGO_COMPACT_ROW = 'contents';
export const LOGO_COMPACT_IMG_PX = 36;
export const LOGO_COMPACT_IMG_CLASS = 'object-contain h-9 w-9 flex-shrink-0 row-span-2';
// From lg the live line is hidden, so the wordmark spans both rows and centres on the logo.
export const LOGO_COMPACT_TEXT_CLASS = 'col-start-2 self-end lg:row-span-2 lg:self-center text-lg leading-tight';

export const MOBILE_SUBTITLE_WRAP = 'lg:hidden col-start-2 self-start';
export const MOBILE_SUBTITLE_LINE =
  'flex items-center gap-1.5 h-4 text-xs text-jc-ink-secondary tabular-nums whitespace-nowrap [&_b]:font-display [&_b]:font-bold [&_b]:text-jc-ink';
export const MOBILE_SUBTITLE_DOT = 'w-1.5 h-1.5 rounded-full bg-jc-live flex-shrink-0';

// ── Search command bar ("Sage" redesign, 2026-09-28). One rounded field that holds every search
// control: the What slot (the part that opens the search panel), the Where chip, the filters chip
// and the forest count button, plus a 2px narrowing line along the bottom edge. Shared by
// Autocomplete.tsx (SRP + LiveLauncher), SearchLauncher.tsx's FallbackLauncher and
// ResourceNavbar.tsx's prerendered twin, so all three render the same class strings (the
// primary-nav-uniformity drift guard pins every part that carries a data-jc-nav-part). ──
export type SearchBarSizeVariant = 'compact40' | 'compact44';
/** `compact40` is the desktop row-one bar (46px since the Sage redesign: it holds two-line slots
 * and 38px chips, inside the 58px row). `compact44` is the mobile bottom bar's single-line field
 * (44px tap target, no chips). `isOpen` flattens the bottom corners for the static twin's
 * attached suggestions dropdown. */
export function searchWrapperClass(sizeVariant: SearchBarSizeVariant, isOpen = false): string {
  const size = sizeVariant === 'compact44' ? 'h-11 pl-4 pr-1.5 max-w-[600px]' : 'h-12 pl-5 pr-1 max-w-[720px]';
  const shape = isOpen ? 'rounded-t-3xl rounded-b-none border-b-0' : 'rounded-full';
  return `relative min-w-0 flex-1 flex items-center gap-1 ${size} ${shape} border border-jc-border-control bg-jc-surface shadow-jc-sm overflow-hidden transition-[box-shadow,border-color] duration-(--jc-duration-base) ease-jc-out motion-reduce:transition-none hover:border-jc-focus/40 focus-within:border-jc-focus focus-within:ring-2 focus-within:ring-jc-focus focus-within:shadow-jc-md`;
}
export const SEARCH_ICON_WRAP = 'pr-2 flex-shrink-0 text-jc-accent flex items-center';
/** The What slot: the clickable region that opens the search panel (a <button> in the hydrated
 * hosts, a <label> for the input in the static twin). Column: small label over the value line. */
export const SEARCH_WHAT_SLOT =
  'flex flex-1 min-w-0 h-full items-center gap-2 text-left cursor-text focus:outline-none';
export const SEARCH_WHAT_TEXT = 'flex flex-1 min-w-0 flex-col justify-center';
/** "What" / "Where" labels: screen-reader only since 2026-10-12 (the placeholder and the chip's
 * value already say what each part is), so every slot is one line at every width. */
export const SEARCH_SLOT_LABEL = 'sr-only';
/** Wraps the value (input or placeholder span) so it keeps a fixed single-line height whether or
 * not the label above it shows. */
export const SEARCH_VALUE_LINE = 'flex items-center min-w-0 h-6';
/** The input (static twin, mobile shell) or its closed-bar span twin. `text-left` is load-bearing
 * on the span twin, which sits inside a <button> with UA-centered text. */
export const SEARCH_INPUT =
  'min-w-0 flex-1 w-full h-full border-0 bg-transparent p-0 text-base text-jc-ink placeholder:text-jc-ink-secondary placeholder:font-normal focus:outline-none text-left truncate';
/** Placeholder look for the closed bar's span twin (an <input>'s own placeholder styling can't apply). */
export const SEARCH_PLACEHOLDER_TEXT = 'font-normal text-jc-ink-secondary';
/** Blinking caret after the typewriter teaser's text. */
export const SEARCH_TEASER_CARET =
  'inline-block w-px h-[16px] ml-px align-[-2px] bg-jc-ink motion-safe:animate-[jc-caret_1s_steps(1)_infinite]';

/** Field peek: a stack of field tiles at the end of the What slot (xl, idle only), the same
 * field glyphs the panel's "Browse a field" grid uses. Hidden while the teaser types. */
export function searchFieldPeekClass(visible: boolean): string {
  return `hidden xl:flex items-center shrink-0 mr-1 transition-[opacity,transform] duration-(--jc-duration-base) ease-jc-out motion-reduce:transition-none ${
    visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2 pointer-events-none'
  }`;
}
export const SEARCH_FIELD_PEEK_TILE =
  'w-6 h-6 -ml-1.5 first:ml-0 rounded-full bg-jc-elevated text-jc-accent ring-2 ring-jc-surface flex items-center justify-center';

/** Where chip. Icon-only circle below xl, two-line (label + value) pill at xl.
 *
 * The `!` on fill and padding here and on the two buttons below is load-bearing on prerendered
 * pages: landing.css's scoped preflight (`.lp-root :where(button) { background: none; padding:
 * 0 }`) is unlayered, so it beats Tailwind's layered utilities and left the count button as white
 * text on nothing (same fix as suggestionsRowClass). */
export function locationSegmentClass(
  sizeVariant: SearchBarSizeVariant,
  state: 'idle' | 'set' | 'excluded' = 'idle',
): string {
  const size = sizeVariant === 'compact44' ? 'h-9 min-w-[36px]' : 'h-10 min-w-10';
  const ring =
    state === 'set'
      ? 'ring-1 ring-inset ring-jc-accent/60'
      : state === 'excluded'
        ? 'ring-1 ring-inset ring-jc-ink'
        : '';
  return `flex flex-col items-center justify-center xl:items-start shrink-0 ${size} px-0! xl:px-4! rounded-full bg-jc-elevated! hover:bg-jc-elevated-hover! text-left transition-colors duration-(--jc-duration-fast) cursor-pointer ${ring}`;
}
export function locationChipValueClass(state: 'idle' | 'set' | 'excluded' = 'idle'): string {
  const tone = state === 'excluded' ? 'text-jc-ink line-through' : 'text-jc-ink';
  return `flex items-center gap-1.5 text-sm leading-5 font-semibold whitespace-nowrap ${tone}`;
}
export const LOCATION_CHIP_TEXT = 'hidden xl:inline max-w-[150px] truncate';

/** Filters chip: icon circle; widens to hold the applied-filter count. */
export function searchFiltersChipClass(hasCount: boolean): string {
  return `relative flex items-center justify-center gap-1.5 shrink-0 h-10 ${
    hasCount ? 'pl-3! pr-2.5!' : 'w-10 p-0!'
  } rounded-full bg-jc-elevated! hover:bg-jc-elevated-hover! text-jc-ink! transition-colors duration-(--jc-duration-fast) cursor-pointer`;
}
export const SEARCH_FILTERS_CHIP_COUNT =
  'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-jc-action text-jc-on-action text-jc-badge font-bold tabular-nums';

/** Forest count button: "18 432 jobs →". Collapses to a round search button when no count is
 * known yet (never a made-up number). */
export function searchGoButtonClass(hasCount: boolean): string {
  return `flex items-center justify-center gap-1.5 shrink-0 h-10 ${
    hasCount ? 'pl-5! pr-4!' : 'w-10 p-0!'
  } rounded-full bg-jc-cta! hover:bg-jc-cta-hover! text-jc-on-cta transition-colors duration-(--jc-duration-fast) cursor-pointer`;
}
export const SEARCH_GO_COUNT = 'font-display font-bold text-base leading-none tabular-nums tracking-[-0.01em]';
export const SEARCH_GO_SUFFIX = 'hidden xl:inline text-sm leading-none font-semibold text-jc-on-cta/85';

/** The narrowing line: 2px along the bar's bottom edge, fill = current count / all open jobs.
 * Only visible once something is narrowed (a filtered SRP, or the teaser typing): at 100% it
 * would just read as a stray underline. */
export const SEARCH_METER_TRACK =
  'pointer-events-none absolute left-6 right-6 bottom-0 h-[2px] rounded-full bg-jc-elevated overflow-hidden transition-opacity duration-(--jc-duration-slow) motion-reduce:transition-none';
export const SEARCH_METER_FILL =
  'block h-full rounded-full bg-jc-accent transition-[width] duration-(--jc-duration-slow) ease-jc-out motion-reduce:transition-none';

// ── Language track (matches LanguageSelector.tsx sizeVariant="compact40") ──
export const LANG_TRACK =
  'hidden xl:flex items-center gap-1 h-10 px-1.5 rounded-full border border-jc-border/70 bg-jc-surface/90';
export const LANG_CIRCLE =
  'relative w-[30px] h-[30px] shrink-0 rounded-full overflow-hidden flex items-center justify-center text-xs font-bold uppercase cursor-pointer transition-colors';
export const LANG_FLAG_HALO = 'absolute inset-0 opacity-30';
export const LANG_TEXT_ACTIVE =
  'relative text-jc-ink [text-shadow:0_0_4px_var(--jc-surface),0_0_4px_var(--jc-surface),0_0_6px_var(--jc-surface)]';
export const LANG_TEXT_INACTIVE = 'relative text-jc-ink-secondary hover:text-jc-ink';

// ── Account pill (matches nav-live-index/AccountPill.tsx's guest branch) ──
export const ACCOUNT_PILL =
  'inline-flex items-center gap-1.5 h-10 pl-1 pr-3.5 rounded-full border border-jc-border bg-jc-surface text-sm font-semibold text-jc-ink-secondary hover:bg-jc-elevated hover:text-jc-ink transition-colors whitespace-nowrap cursor-pointer';
export const ACCOUNT_AVATAR_GUEST =
  'flex-shrink-0 w-[30px] h-[30px] rounded-full border-[1.5px] border-dashed border-jc-ink-secondary text-jc-ink-secondary flex items-center justify-center';
export const ACCOUNT_AVATAR_MEMBER =
  'flex-shrink-0 overflow-hidden rounded-full w-[30px] h-[30px] flex items-center justify-center';
/** Matches AccountPill.tsx's live badge (new-matches count for a signed-in member). Shared here
 * (TEC navbar redesign item 3) so the static header's script-rendered badge, filled from
 * GET /api/my-jobs-badge, can never drift from the hydrated pill's own badge markup. */
export const ACCOUNT_BADGE =
  'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-jc-action text-jc-on-action text-jc-badge font-bold tabular-nums';

// ── Mobile bottom bar (TEC navbar redesign item 2; matches components/navigation/
// MobileBottomNav.tsx, extracted here so the static header (ResourceNavbar.tsx's twin, built by
// web-static/pages/landing/landing-nav-live-index-script.ts) can render the same chrome instead
// of a hand-drifted approximation). Button sizing itself stays a `liveIndex ? 'w-11 h-11' :
// 'p-3'` ternary in MobileBottomNav.tsx (unchanged, kept there rather than folded in here) since
// the flag-off classic size must keep rendering exactly as it did before this ticket; only the
// border/background/hover chrome common to both sizes lives here. ──
/** The bottom bar's font: the platform sans (the same system stack the body uses), not a
 * hard-coded family. Kept as a separate export so MobileBottomNav.tsx's own `iosFontStack`
 * variable can import it. */
export const MOBILE_BOTTOM_BAR_FONT_STACK = 'font-sans';
/** Flat, like the design system's TabBar: a surface with a hairline on top, no lifted sheet. */
export const MOBILE_BOTTOM_BAR_CONTAINER = `fixed bottom-0 left-0 w-full z-(--jc-z-tabbar) flex items-center gap-2 px-4 pt-2 border-t border-jc-border lg:hidden bg-jc-surface ${MOBILE_BOTTOM_BAR_FONT_STACK} max-w-full overflow-hidden`;
/** The design system's outline IconButton, at the 44px tap size. */
const MOBILE_BOTTOM_BAR_ICON_BUTTON =
  'inline-flex shrink-0 items-center justify-center rounded-jc-control bg-jc-surface ring-1 ring-inset ring-jc-border-control text-jc-ink-secondary transition duration-(--jc-duration-fast) ease-jc-out hover:bg-jc-elevated hover:text-jc-ink active:scale-97 motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus cursor-pointer';
export const MOBILE_BOTTOM_BAR_FILTERS_BUTTON_BASE = `relative ${MOBILE_BOTTOM_BAR_ICON_BUTTON}`;
/** Filters applied: the selected role, like any other toggled control. */
export const MOBILE_BOTTOM_BAR_FILTERS_ACTIVE = 'bg-jc-selected! text-jc-on-selected! ring-0!';
/** Applied-filter count on the filters button (the TabBar badge). */
export const MOBILE_BOTTOM_BAR_BADGE =
  'absolute -top-1 -right-1 inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-jc-ink px-1 text-xs font-semibold tabular-nums text-jc-on-solid ring-2 ring-jc-surface';
export const MOBILE_BOTTOM_BAR_MENU_BUTTON_BASE = MOBILE_BOTTOM_BAR_ICON_BUTTON;
/** The 44px size both buttons always use once the navbar live-index redesign is the only header
 * (the static twin, unlike MobileBottomNav.tsx, has no flag-off classic path: prerendered pages
 * always ship the new design, see ResourceNavbar.tsx's own doc comment). */
export const MOBILE_BOTTOM_BAR_BUTTON_SIZE = 'w-11 h-11';

// ── Menu button (matches nav-live-index/BarsMenuButton.tsx) ──
export const MENU_BUTTON_BASE =
  'inline-flex shrink-0 items-center justify-center rounded-jc-control bg-jc-surface ring-1 ring-inset ring-jc-border-control text-jc-ink-secondary transition duration-(--jc-duration-fast) ease-jc-out hover:bg-jc-elevated hover:text-jc-ink active:scale-97 motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus cursor-pointer';
export const MENU_BUTTON_SIZE_DESKTOP = 'w-10 h-10';
export const MENU_BUTTON_SIZE_MOBILE = 'w-11 h-11 lg:w-10 lg:h-10';

// ── Row two index strip (matches nav-live-index/IndexStrip.tsx). Part of the white navbar, inset
// like the row above, and set in the design system's type roles at one size (type-meta, 14px, the
// row's own control size): the live figures read as a readout (display face, bold, then a secondary
// label), the section words after the divider are nav links (semibold). The current page is ink
// with the forest underline. ──
export const STRIP_CONTAINER =
  'jc-strip hidden lg:flex items-center border-t border-jc-border bg-jc-surface overflow-hidden px-10 xl:px-16 transition-[height,opacity] duration-(--jc-duration-base) ease-jc-out motion-reduce:transition-none';
export const STRIP_SCROLLER =
  'flex items-center gap-5 min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden flex-1';
export function stripFigureLinkClass(isActive: boolean): string {
  return `group relative inline-flex items-baseline gap-1 whitespace-nowrap py-1.5 type-meta no-underline ${
    isActive ? 'text-jc-ink' : 'text-jc-ink-secondary hover:text-jc-ink'
  }`;
}
export function stripFigureValueClass(_isActive: boolean): string {
  return 'inline-block empty:min-w-[5ch] font-display font-bold tabular-nums text-jc-ink';
}
export function stripUnderlineClass(isActive: boolean): string {
  return `pointer-events-none absolute left-0 right-0 bottom-0 h-0.5 bg-jc-accent transition-transform duration-(--jc-duration-fast) ease-jc-out origin-left ${
    isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
  }`;
}
export function stripWordLinkClass(isActive: boolean): string {
  return `group relative inline-flex items-baseline whitespace-nowrap py-1.5 type-meta font-semibold! no-underline ${
    isActive ? 'text-jc-ink' : 'text-jc-ink-secondary hover:text-jc-ink'
  }`;
}
export const STRIP_DIVIDER = 'jc-strip-divider w-px h-4 bg-jc-border-control flex-shrink-0 mx-1';
/** The live figures and the section words, as two groups: one row by default; from 1360px the
 * figures span the search field above and the words sit right-aligned (index-strip.css). */
export const STRIP_FIGURES_GROUP = 'jc-strip-figures flex items-center gap-5';
export const STRIP_WORDS_GROUP = 'jc-strip-words flex items-center gap-5';
export const STRIP_STAMP_WRAP =
  'ml-auto inline-flex items-center gap-1.5 whitespace-nowrap pl-4 type-caption text-jc-ink-secondary flex-shrink-0';
export const STRIP_STAMP_DOT =
  'size-1.5 rounded-full bg-jc-live motion-safe:animate-[ping_2.4s_ease-out_1] motion-reduce:animate-none';

// ── Search-suggestions dropdown (TEC navbar redesign Phase 4 item 2), shaped after the SRP's
// own Autocomplete popover (components/Autocomplete.tsx) and shared by the React fallback
// launcher (FallbackLauncher in SearchLauncher.tsx) and the static launcher's vanilla-JS island
// (web-static/pages/landing/landing-nav-live-index-script.ts), so the two dropdowns look alike.
//
// Visual-parity pass (2026-09-09): the original version above was only shaped like Autocomplete's
// popover in structure (header + rows), not in treatment, so the two dropdowns read as visibly
// different controls. This ports Autocomplete's frosted-glass rounded-3xl panel (backdrop blur,
// 2px border, inset shadow, left/right edge-fade gradients, "scroll for more" hint past
// SUGGESTIONS_SCROLL_HINT_THRESHOLD rows) so this simplified dropdown looks like the same
// component, not a plainer stand-in. Autocomplete.tsx itself stays untouched (hardcoded hex, no
// shared-styles dependency, per the original Phase 4 spec): these are token-based equivalents,
// not a byte-for-byte class copy. ──
export const SUGGESTIONS_PANEL =
  'absolute left-0 right-0 top-full z-(--jc-z-dropdown) max-h-96 overflow-y-auto rounded-b-3xl rounded-t-none border-l-2 border-r-2 border-b-2 border-jc-border bg-jc-surface/95 backdrop-blur-xl shadow-jc-lg';
/** Wraps the header + rows inside SUGGESTIONS_PANEL; the edge-fade gradients below are
 * absolutely positioned against this element (not the scrollable panel itself), matching
 * Autocomplete's own `<div className="relative py-2">` content wrapper. */
export const SUGGESTIONS_LIST_WRAP = 'relative py-2';
export const SUGGESTIONS_EDGE_FADE_LEFT =
  'absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-jc-surface/95 to-transparent pointer-events-none z-10';
export const SUGGESTIONS_EDGE_FADE_RIGHT =
  'absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-jc-surface/95 to-transparent pointer-events-none z-10';
export const SUGGESTIONS_HEADER =
  'px-5 pt-3 pb-2 text-xs font-semibold uppercase tracking-wider text-jc-ink-secondary flex items-center justify-between';
/** The "Scroll for more" hint shown next to the header once there are more rows than fit without
 * scrolling; matches Autocomplete's own `facetSuggestions.length > 5` threshold. */
export const SUGGESTIONS_SCROLL_HINT_THRESHOLD = 5;
export const SUGGESTIONS_HEADER_HINT = 'text-xs normal-case font-normal text-jc-ink-secondary';
export function suggestionsRowClass(isActive: boolean): string {
  // `!` (important) on the padding utilities: this row is a <button>, and on the static/prerendered
  // page it renders inside `.lp-root`, which carries a preflight-style reset (landing.css:
  // `.lp-root :where(button) { padding: 0 }`) that ties `px-5`/`py-3` on specificity and wins on
  // source order, silently zeroing the row's padding. components/Autocomplete.tsx's real popover
  // never hits this (it isn't nested under `.lp-root`), which is why only this dropdown went flat.
  return `flex w-full items-center justify-between gap-3 px-5! py-3! text-left text-xs font-normal transition-all duration-(--jc-duration-base) ${
    isActive ? 'bg-jc-elevated/80 text-jc-ink' : 'text-jc-ink hover:bg-jc-elevated/80 active:bg-jc-elevated-hover'
  }`;
}
export const SUGGESTIONS_ROW_ATTR_LABEL = 'text-xs uppercase tracking-wide text-jc-ink-secondary font-semibold';
export const SUGGESTIONS_ROW_COUNT = 'text-sm font-semibold text-jc-ink-secondary tabular-nums';
/** Leading tile on a suggestion row: the company logo for `companyId` rows, a facet icon otherwise. */
export const SUGGESTIONS_ROW_LEAD =
  'flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-jc-elevated text-jc-ink-secondary';
export const SUGGESTIONS_ROW_LEAD_ICON = 'w-4 h-4';
export const SUGGESTIONS_ROW_LEAD_LOGO = 'h-8 w-8 object-contain';
/** Left group of a row (lead tile + labels); `min-w-0` so long labels truncate instead of pushing the count out. */
export const SUGGESTIONS_ROW_MAIN = 'flex min-w-0 items-center gap-3';
export const SUGGESTIONS_ROW_TEXT = 'flex min-w-0 flex-col';
export const SUGGESTIONS_EMPTY = 'px-5 py-4 text-center text-sm text-jc-ink-secondary';
